import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import Logo from "@/assets/icons/logo-steto-light-full.svg?url";
import { Images } from "@/assets/images.tsx";
import { useKeycloak } from "@/auth/KeycloakAuthState.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

const LOGO_MAX_WIDTH_PX = 300;
const BADGE_IMG_HEIGHT_PX = 48;

const APP_STORE_URL = "https://apps.apple.com/fr/app/steto/id6448745966";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=care.steto.android";

export const Login: React.FC = () => {
  const keycloak = useKeycloak(state => state.keycloak);

  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div className={styles.container}>
      <img className={styles.logo} src={Logo} alt="Steto" />

      <div className={styles.choices}>
        <div className={styles.doctor}>
          <Typography variant="h4" className={styles.columnTitle}>
            {t("pages.login.practitionerTitle")}
          </Typography>
          <div className={styles.actions}>
            <Button
              variant="contained"
              className={styles.connectionButton}
              data-action="auth.session.login"
              onClick={() =>
                keycloak.login({
                  redirectUri: window.origin,
                })
              }
            >
              {t("pages.login.connection")}
            </Button>
          </div>
          <Typography variant="bodySmall" className={styles.footerText}>
            {t("pages.login.practitionerNoAccount")}
          </Typography>
        </div>
        <div className={styles.patient}>
          <Typography variant="h4" className={cx(styles.columnTitle)}>
            {t("pages.login.patientTitle")}
          </Typography>
          <div className={styles.actions}>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className={styles.badgeLink}>
              <Images.appStoreBadge alt="App Store" className={styles.badgeImg} />
              <Typography variant="caption" className={styles.badgeLabel}>
                {t("pages.login.iphone")}
              </Typography>
            </a>
            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className={styles.badgeLink}>
              <Images.googlePlayBadge alt="Google Play" className={styles.badgeImg} />
              <Typography variant="caption" className={styles.badgeLabel}>
                {t("pages.login.otherSmartphone")}
              </Typography>
            </a>
          </div>
          <Typography variant="bodySmall" className={styles.footerText}>
            {t("pages.login.patientInstructions")}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(16, 16),
    width: "100%",
    minHeight: "100vh",
    gap: theme.spacing(24),
  }),
  logo: css({
    width: `min(80vw, ${LOGO_MAX_WIDTH_PX}px)`,
    margin: theme.spacing(0, 24, 24, 24),
  }),
  choices: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
    rowGap: theme.spacing(24),
    columnGap: theme.spacing(48),
    flexWrap: "wrap",
  }),
  doctor: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(8),
    maxWidth: theme.spacing(256),
  }),
  patient: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(8),
    maxWidth: theme.spacing(256),
  }),
  columnTitle: css({
    color: theme.palette.common.white,
    textAlign: "center",
  }),
  actions: css({
    height: theme.spacing(36),
    display: "flex",
    gap: theme.spacing(8),
  }),
  connectionButton: css({
    padding: theme.spacing(4, 8),
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    alignSelf: "center",
  }),
  badgeLink: css({
    cursor: "pointer",

    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    textDecoration: "none",
  }),
  badgeImg: css({
    height: `${BADGE_IMG_HEIGHT_PX}px`,
    width: "auto",
  }),
  badgeLabel: css({
    color: theme.palette.common.white,
    textAlign: "center",
  }),

  footerText: css({
    color: theme.palette.common.white,
    textAlign: "center",
    flex: "1 1 0",
    whiteSpace: "pre-line",
    maxWidth: "80%",
  }),
});
